import { useDispatch } from 'react-redux';
import { wrapService } from 'actions/service_wrapper.actions';
import { useLogger } from 'hook/error.hook';
import { useComplexState } from 'hook/helper.hook';
import { fetchInsurances } from 'externals/_services/product.service';
import {
  AUTO_INSURANCE_CHECK_BOX_GROUP,
  GOOD_DRIVER,
  MULTIPLE_VEHICLES,
  HOMEOWNER,
} from 'partial/OfferManager/modules/AutoInsuranceModule/helpers/AutoInsurance.helpers';
import { PLACEMENT } from 'pages/Marketplace/InsuranceMarketplace.types';
import {
  cachePayloadForKey,
  AUTO_INSURANCE_KEY,
} from 'actions/datacache.actions';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { AUTO_INSURANCE_VERTICAL } from 'types/marketplaceTiles.constants';

const a_fetchMarketplaceAutoInsurances = wrapService(fetchInsurances, {
  name: 'FETCH_AUTO_INSURANCE_OCF',
});

export const useAutoInsuranceOcf = (
  autoInsuranceConfig,
  userInfo,
  eventHandler,
  items,
) => {
  const logger = useLogger({
    name: 'autoInsuranceOcf.hook.js',
    categories: ['ocf'],
  });

  const dispatch = useDispatch();

  const [states, setStates] = useComplexState({
    zipCode: userInfo?.zip,
    currentIssuer: autoInsuranceConfig?.issuerList[0],
    multiChoiceAnswers: [GOOD_DRIVER],
    fetching: false,
    insuranceResponse: { items: items },
    showFilters: true,
  });

  let currentlyInsured = states.currentIssuer !== 'Not Currently Insured';

  const onSelectMultiQuestionOptions = func => {
    setStates({
      multiChoiceAnswers: func(states.multiChoiceAnswers),
    });
  };

  const handleShowFilters = () => {
    setStates({
      showFilters: !states.showFilters,
    });
  };

  const isInCheckBoxGroup = value => {
    return states.multiChoiceAnswers.includes(value);
  };

  const onAutoInsuranceSearch = () => {
    const mixpanelCheckBox = [];
    states.multiChoiceAnswers.forEach((item, index) => {
      const choice = AUTO_INSURANCE_CHECK_BOX_GROUP.find(e => e.value === item);
      if (choice) {
        mixpanelCheckBox.push(choice.label);
      }
    });

    setStates({
      fetching: true,
    });

    retriveAutoInsurance()
      .then(insuranceResponse => {
        setStates({
          insuranceResponse,
        });
        dispatch(
          cachePayloadForKey(AUTO_INSURANCE_KEY, {
            offers: insuranceResponse?.items,
          }),
        );
        setStates({
          fetching: false,
        });
      })
      .catch(err => {
        logger.reportAPIError(err);
        logger.showError('Unable to retrive offers', err.message);
        setStates({
          fetching: false,
        });
      });
    eventHandler(CLICK, {
      'Click Type': 'Update',
      Insurer: states.currentIssuer,
      CheckBox: mixpanelCheckBox,
      Vertical: AUTO_INSURANCE_VERTICAL,
    });
  };

  const retriveAutoInsurance = () => {
    let filters = {
      currentlyInsured: currentlyInsured,
      homeowner: isInCheckBoxGroup(HOMEOWNER),
      insuranceType: 'AUTO',
      userId: 'me',
      multipleVehicles: isInCheckBoxGroup(MULTIPLE_VEHICLES),
      goodDriver: isInCheckBoxGroup(GOOD_DRIVER),
      zip: states.zipCode,
      carrier: states.currentIssuer,
      placement: PLACEMENT.offers,
    };

    return new Promise((resolve, reject) => {
      dispatch(a_fetchMarketplaceAutoInsurances(filters))
        .then(autoInsuranceRes => resolve(autoInsuranceRes))
        .catch(reject);
    });
  };

  return {
    states,
    setStates,
    checkBoxList: AUTO_INSURANCE_CHECK_BOX_GROUP,
    onSelectMultiQuestionOptions,
    handleShowFilters,
    onAutoInsuranceSearch,
  };
};
